import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Sparkles, Plus, Lock, Trash2, Clock } from 'lucide-react';
import Header from '@/components/Header';
import PageSubHeader from '@/components/PageSubHeader';
import StudyPlanCard from '@/components/StudyPlanCard';
import CustomPlanWizardModal from '@/components/CustomPlanWizardModal';
import { useSafeBack } from '@/utils/useSafeBack';
import { API_URL as API } from '@/utils/apiConfig';
import useCachedFetch from '@/hooks/useCachedFetch';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';
import { toast } from 'sonner';

import { useTranslation } from 'react-i18next';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'mine', label: 'My Custom' },
  { id: 'curated', label: 'Curated' },
  { id: 'topical', label: 'Topical' },
  { id: 'book', label: 'Book Study' },
  { id: 'long-form', label: 'Long-form' },
];

export default function StudyPlansListPage() {
  const {
    t,
    i18n
  } = useTranslation();

  const navigate = useNavigate();
  const goBack = useSafeBack('/app');
  const [filter, setFilter] = useState('all');
  const [wizardOpen, setWizardOpen] = useState(false);
  const { tier, loading: subLoading } = useSubscription();
  // Explicit list of paying tiers. Legacy `paid` is included so the
  // long-tail of grand-fathered subscribers isn't locked out.
  const PAYING_TIERS = ['premium', 'share', 'paid'];
  const isPaying = PAYING_TIERS.includes(tier);
  const tierUnknown = subLoading || tier === null;
  const { enabled: customPlansEnabled, loaded: flagReady } = useFeatureFlag('custom_study_plans');
  // Until flags load, keep the card visible so paying users don't
  // see a flash of "missing" UI on cold-launch. Once flags load, the
  // admin's toggle wins.
  const showCustomCta = !flagReady || customPlansEnabled;
  const lang = (i18n.language || 'en').split('-')[0];

  // Combined cached fetch — the three endpoints are fired in parallel
  // and cached under one key so cold-launches never see a spinner
  // after the first visit and re-opens hydrate instantly.
  const fetcher = useCallback(async () => {
    const [allRes, mineRes, customRes] = await Promise.all([
      axios.get(`${API}/reading-plans`, { params: { locale: lang }, withCredentials: true }),
      axios.get(`${API}/reading-plans/me/active`, { withCredentials: true }).catch(() => ({ data: { plans: [] } })),
      axios.get(`${API}/reading-plans/me/custom`, { withCredentials: true }).catch(() => ({ data: { plans: [] } })),
    ]);
    return {
      plans: allRes.data?.plans || [],
      activePlans: mineRes.data?.plans || [],
      customPlans: customRes.data?.plans || [],
    };
  }, [lang]);
  const { data, isColdLoading, refetch } = useCachedFetch(`plans:list:${lang}`, fetcher, {
    fallback: { plans: [], activePlans: [], customPlans: [] },
  });
  const plans = data?.plans || [];
  const activePlans = data?.activePlans || [];
  const customPlans = data?.customPlans || [];
  const buildingPlans = customPlans.filter((p) => p.build_status === 'building');
  const readyCustomPlans = customPlans.filter((p) => p.build_status === 'ready');
  const failedCustomPlans = customPlans.filter((p) => p.build_status === 'failed');

  // Poll every 5s while any plan is still building so the UI reveals
  // the freshly finished plan (and the push notification arrives) the
  // moment Eili finishes.
  React.useEffect(() => {
    if (buildingPlans.length === 0) return undefined;
    const id = setInterval(() => { refetch(); }, 5000);
    return () => clearInterval(id);
  }, [buildingPlans.length, refetch]);

  const handleDeleteCustom = async (planId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this custom plan? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/reading-plans/custom/${planId}`, { withCredentials: true });
      toast.success('Custom plan deleted');
      refetch();
    } catch {
      toast.error('Could not delete the plan');
    }
  };

  const visiblePlans = useMemo(() => {
    if (filter === 'mine') return readyCustomPlans;
    if (filter === 'all') return [...readyCustomPlans, ...plans];
    return plans.filter((p) => p.category === filter);
  }, [plans, readyCustomPlans, filter]);

  return (
    <div className="page-container" style={{ background: 'var(--theme-background)' }}>
      <Header />
      <PageSubHeader
        onBack={goBack}
        title={t('generated.studyplanslistpage_study_plans_5c3d')}
        subtitle="Guided journeys through Scripture, paced by you"
        backTestId="study-plans-back"
      />
      <div className="page-content max-w-2xl lg:max-w-5xl xl:max-w-6xl mx-auto px-4 lg:px-8 pt-4 lg:pt-8 pb-24 w-full">
        {/* Create your own — headline CTA */}
        {showCustomCta && (
        <button
          onClick={() => {
            // Wait for tier to resolve so paying users never get
            // wrongly routed to the paywall while their subscription
            // context is still loading.
            if (tierUnknown) return;
            if (isPaying) {
              setWizardOpen(true);
            } else {
              navigate('/plans');
            }
          }}
          disabled={tierUnknown}
          className="group w-full mb-6 rounded-2xl p-5 flex items-center gap-4 text-left transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-wait"
          style={{
            background: 'linear-gradient(135deg, var(--theme-accent) 0%, color-mix(in oklab, var(--theme-accent) 60%, black) 100%)',
            color: '#fff',
            boxShadow: '0 14px 32px -12px color-mix(in oklab, var(--theme-accent) 60%, black)',
          }}
          data-testid="create-custom-plan-cta"
        >
          <div
            className="shrink-0 h-12 w-12 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.18)' }}
          >
            {tierUnknown ? <Loader2 className="h-5 w-5 animate-spin" /> : isPaying ? <Sparkles className="h-6 w-6" /> : <Lock className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest opacity-80 mb-0.5">Powered by Eili</p>
            <p className="text-base font-semibold leading-tight">Build your own study plan</p>
            <p className="text-xs opacity-85 mt-0.5">
              {tierUnknown
                ? 'Checking your plan…'
                : isPaying
                  ? "Pick a topic, length and cadence — Eili writes a biblically grounded plan for you."
                  : 'Available on Premium and Share plans. Tap to upgrade.'}
            </p>
          </div>
          <Plus className="h-5 w-5 opacity-80 group-hover:opacity-100 shrink-0" />
        </button>
        )}

        {/* In-progress custom plans — always shown at the top so the
            user has visual confirmation that Eili is still building. */}
        {buildingPlans.length > 0 && (
          <section className="mb-6" data-testid="building-custom-plans">
            {buildingPlans.map((p) => (
              <div
                key={p.plan_id}
                className="rounded-2xl p-4 mb-3 flex items-center gap-3"
                style={{
                  background: 'var(--glass-bg)',
                  border: '1px dashed color-mix(in oklab, var(--theme-accent) 45%, transparent)',
                }}
                data-testid={`building-plan-${p.plan_id}`}
              >
                <div className="relative shrink-0 h-11 w-11 rounded-xl flex items-center justify-center"
                     style={{ background: 'color-mix(in oklab, var(--theme-accent) 18%, transparent)' }}>
                  <Sparkles className="h-5 w-5 animate-pulse" style={{ color: 'var(--theme-accent)' }} />
                  <span className="absolute inset-0 rounded-xl border-2 border-transparent"
                        style={{
                          borderTopColor: 'var(--theme-accent)',
                          animation: 'spin 1.6s linear infinite',
                        }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                    Eili is building your plan on <span style={{ color: 'var(--theme-accent)' }}>{p.build_topic || 'your topic'}</span>
                  </p>
                  <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: 'var(--theme-textSecondary)' }}>
                    <Clock className="h-3 w-3" />
                    Usually ready in 1–5 minutes. Keep using the app — we'll notify you.
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteCustom(p.plan_id)}
                  className="p-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
                  aria-label="Cancel"
                  data-testid={`cancel-building-plan-${p.plan_id}`}
                >
                  <Trash2 className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
                </button>
              </div>
            ))}
          </section>
        )}

        {/* Failed plan surfaces so the user isn't left wondering */}
        {failedCustomPlans.length > 0 && (
          <section className="mb-6 space-y-2" data-testid="failed-custom-plans">
            {failedCustomPlans.map((p) => (
              <div
                key={p.plan_id}
                className="rounded-2xl p-4 flex items-center gap-3"
                style={{
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.3)',
                }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: '#ef4444' }}>
                    Couldn't build a plan on "{p.build_topic}"
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
                    Try a more specific topic or a different angle.
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteCustom(p.plan_id)}
                  className="p-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
                  aria-label="Dismiss"
                  data-testid={`dismiss-failed-plan-${p.plan_id}`}
                >
                  <Trash2 className="h-4 w-4" style={{ color: '#ef4444' }} />
                </button>
              </div>
            ))}
          </section>
        )}

        {/* Active plans rail */}
        {activePlans.length > 0 && (
          <section className="mb-8" data-testid="study-plans-active-section">
            <h2 className="text-sm font-semibold mb-3" style={{ color: 'var(--theme-text)' }}>
              {t('generated.studyplanslistpage_continue_4fee')}
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
              {activePlans.map((plan) => (
                <div key={plan.plan_id} className="snap-start">
                  <StudyPlanCard
                    plan={plan}
                    progress={plan.progress}
                    onClick={() => navigate(`/study-plans/${plan.plan_id}`)}
                    compact
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Filter chips */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1 -mx-4 px-4">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className="text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-all"
              style={{
                background: filter === f.id ? 'var(--theme-accent)' : 'var(--glass-bg)',
                color: filter === f.id ? '#fff' : 'var(--theme-textSecondary)',
                border: `1px solid ${filter === f.id ? 'var(--theme-accent)' : 'var(--glass-border)'}`,
                WebkitTapHighlightColor: 'transparent',
              }}
              data-testid={`study-plans-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Plans grid */}
        {isColdLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
          </div>
        ) : visiblePlans.length === 0 ? (
          <p className="text-sm text-center py-10" style={{ color: 'var(--theme-textSecondary)' }}>
            {filter === 'mine'
              ? "You haven't built a custom plan yet. Tap the button above to start."
              : t('generated.studyplanslistpage_no_plans_in_03d0')}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="study-plans-grid">
            {visiblePlans.map((plan) => (
              <StudyPlanCard
                key={plan.plan_id}
                plan={plan}
                onClick={() => navigate(`/study-plans/${plan.plan_id}`)}
              />
            ))}
          </div>
        )}
      </div>

      <CustomPlanWizardModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => { setWizardOpen(false); refetch(); }}
      />
    </div>
  );
}
